import React from 'react';
import clsx from 'clsx';
import { motion } from 'motion/react';
import {
  DocumentIcon,
  DocumentTextIcon,
  ListBulletIcon,
  PaperClipIcon,
  TableCellsIcon
} from '@heroicons/react/20/solid';
import { SegmentedControl, SegmentedControlOption } from '@oysterjs/ui/segmented-control';

import { CaraWorkspaceArtifactType, type CaraWorkspaceFieldsFragment } from '~/api/graphql';

import { getDisplayLabel } from '~/components/documents/documents.utils';
import { getArtifactTitle } from '~/components/workspaces/lib/artifact';
import { OUTLINE_RAIL_WIDTH } from '~/components/workspaces/lib/artifact-panel-width';
import {
  buildDocumentOutline,
  buildMessageOutline,
  type OutlineEntry
} from '~/components/workspaces/lib/outline';
import {
  documentClientNote,
  useWorkspaceDocumentClients,
  WorkspaceDocumentClientMenu,
  type WorkspaceDocument
} from '~/components/workspaces/workspace-document-clients';
import { FormError } from '~/components/form-error';

type Workspace = CaraWorkspaceFieldsFragment;
type Artifact = Workspace['artifacts'][0];

const HEADING_SELECTOR = 'h1, h2, h3';

/** The composer, which the rail watches the same way it reaches the messages: through the DOM. */
const COMPOSER_SELECTOR = '[data-workspace-composer]';

const normalize = (text: string | null) => (text ?? '').replace(/\s+/g, ' ').trim();

/**
 * A rendered heading's text as the outline knows it.
 *
 * A citation in a heading reaches the DOM as a superscript number inside it,
 * which the label built from the markdown leaves out, so the marker comes out
 * of the copy being read rather than the heading itself.
 */
const headingText = (heading: HTMLElement) => {
  const text = heading.cloneNode(true) as HTMLElement;
  text.querySelectorAll('[data-footnote-ref]').forEach((marker) => marker.remove());
  return normalize(text.textContent);
};

/**
 * The heading element an entry points at.
 *
 * Ordinals come from the markdown, but raw HTML in a response reaches the DOM
 * as a real heading without ever having been an mdast heading node, so the two
 * counts can disagree. The label is what was clicked, so it settles the
 * disagreement.
 */
const resolveHeading = (message: HTMLElement, entry: OutlineEntry): HTMLElement | null => {
  const headings = Array.from(message.querySelectorAll<HTMLElement>(HEADING_SELECTOR));
  const byOrdinal = headings[entry.headingIndex ?? 0] ?? null;
  if (byOrdinal && headingText(byOrdinal) === normalize(entry.label)) {
    return byOrdinal;
  }
  return headings.find((heading) => headingText(heading) === normalize(entry.label)) ?? byOrdinal;
};

/** What an entry points at: the message itself for a prompt, a heading within it otherwise. */
const resolveEntryTarget = (entry: OutlineEntry): HTMLElement | null => {
  const message = document.querySelector<HTMLElement>(
    `[data-message-id="${CSS.escape(entry.messageId)}"]`
  );
  if (!message) {
    return null;
  }
  return entry.headingIndex == null ? message : (resolveHeading(message, entry) ?? message);
};

/**
 * Scrolls the conversation to an outline entry.
 *
 * The rail only exists from `lg` up, where the conversation column owns its own
 * scrolling and nothing sticky covers its top edge, so aligning the target to
 * the start of the scrollport needs no offset. `scrollIntoView` finds that
 * scrollport itself rather than having to be told which element scrolls.
 */
const scrollToEntry = (entry: OutlineEntry) => {
  const target = resolveEntryTarget(entry);
  if (!target) {
    return;
  }
  target.style.scrollMarginTop = '16px';
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const entryKey = (entry: OutlineEntry) => `${entry.messageId}:${entry.headingIndex ?? 'message'}`;

/**
 * The box the conversation scrolls inside, whose top edge is the line anchors
 * disappear over. From `lg` up that is the conversation column; below it the
 * page itself scrolls and there is no such ancestor, leaving the viewport.
 */
const scrollportOf = (element: Element): Element | null => {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
  }
  return null;
};

/**
 * The last anchor to have gone over the top edge.
 *
 * Measured rather than remembered, so that scrolling back up moves the answer
 * back up with it: an anchor that has dropped below the fold again stops
 * counting as passed, which retaining the previous answer would not catch.
 */
const lastAbove = (anchors: Element[], scrollport: Element | null) => {
  const fold = scrollport ? scrollport.getBoundingClientRect().top : 0;
  return anchors.filter((anchor) => anchor.getBoundingClientRect().top < fold).pop();
};

/**
 * The entry the reader is currently at.
 *
 * A tall window shows several anchors at once, and the earliest of them is the
 * one being read -- the rest are what is coming. Document order is the order of
 * `entries`, so the answer comes from walking the anchors in that order rather
 * than from the intersection records, which arrive in the browser's.
 *
 * Reading far enough into a section leaves no anchor in view at all, which is
 * not a move out of that section, so the fallback is the last anchor overhead.
 * Measuring costs a layout read, which is why it waits for the case that needs
 * it instead of being how the answer is found in the first place.
 */
const useCurrentEntry = (entries: OutlineEntry[]): string | null => {
  const [current, setCurrent] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    const anchors = new Map<Element, string>();
    entries.forEach((entry) => {
      const target = resolveEntryTarget(entry);
      if (target) {
        anchors.set(target, entryKey(entry));
      }
    });
    const order = Array.from(anchors.keys());
    const scrollport = order[0] ? scrollportOf(order[0]) : null;

    const onScreen = new Set<Element>();
    const observer = new IntersectionObserver((records) => {
      records.forEach((record) =>
        record.isIntersecting ? onScreen.add(record.target) : onScreen.delete(record.target)
      );
      const at = order.find((anchor) => onScreen.has(anchor)) ?? lastAbove(order, scrollport);
      if (at) {
        setCurrent(anchors.get(at) ?? null);
      }
    });
    order.forEach((anchor) => observer.observe(anchor));
    return () => observer.disconnect();
  }, [entries]);

  return current;
};

/** Indent per heading level. Index 0 is a user prompt, which anchors its turn flush left. */
const DEPTH_PADDING = ['pl-2', 'pl-2', 'pl-6', 'pl-10'];

const OutlineRow = (props: {
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={props.onClick}
    className={clsx(
      'flex w-full items-start gap-2 rounded-md py-1 pr-2 text-left cursor-pointer',
      // 'hover:bg-sky-200/80 dark:hover:bg-sky-800/80 hover:backdrop-blur-md',
      'hover:underline',
      'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
      props.className
    )}
  >
    {props.children}
  </button>
);

const OutlineEmptyState = (props: { children: React.ReactNode }) => (
  <p className="px-2 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
    {props.children}
  </p>
);

const OutlineSectionLabel = (props: { children: React.ReactNode }) => (
  <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
    {props.children}
  </p>
);

/**
 * The conversation's shape: every prompt, and under each the section headings of
 * the response it drew.
 */
const WorkspaceOutlineMessageList = (props: { workspace: Workspace; onSelect?: () => void }) => {
  const entries = React.useMemo(
    () => buildMessageOutline(props.workspace),
    [props.workspace.messages]
  );
  const current = useCurrentEntry(entries);

  if (entries.length === 0) {
    return (
      <OutlineEmptyState>
        Your prompts and the section headings of Cara&apos;s responses will appear here.
      </OutlineEmptyState>
    );
  }

  return (
    <ul className="flex flex-col">
      {entries.map((entry) => (
        <li key={entryKey(entry)}>
          <OutlineRow
            onClick={() => {
              scrollToEntry(entry);
              props.onSelect?.();
            }}
            className={clsx(
              DEPTH_PADDING[Math.min(entry.depth, DEPTH_PADDING.length - 1)],
              entry.depth === 0
                ? 'mt-3 text-xs font-medium text-neutral-900 first:mt-0 dark:text-neutral-100'
                : 'text-xs text-neutral-600 dark:text-neutral-400',
              entryKey(entry) === current && 'underline'
            )}
          >
            <span className="min-w-0 flex-1 truncate" title={entry.label}>
              {entry.label}
            </span>
          </OutlineRow>
        </li>
      ))}
    </ul>
  );
};

const artifactIcon = (type: CaraWorkspaceArtifactType) => {
  switch (type) {
    case CaraWorkspaceArtifactType.XlsxFile:
      return TableCellsIcon;
    case CaraWorkspaceArtifactType.DocumentHtml:
    case CaraWorkspaceArtifactType.DocxFile:
      return DocumentTextIcon;
    default:
      return DocumentIcon;
  }
};

/**
 * The frontend prefixes an attachment's name with a bracketed source tag when it
 * uploads one, the same way the attachment chips under a prompt strip it back off.
 */
const attachmentName = (name: string) => name.replace(/^\[[^\]]+\] /, '');

/** What the workspace produced, and what it was given to work from. */
const WorkspaceOutlineDocumentList = (props: {
  workspace: Workspace;
  onOpenArtifact: (artifact: Artifact) => void;
}) => {
  const { artifacts, files } = React.useMemo(
    () => buildDocumentOutline(props.workspace),
    [props.workspace.artifacts, props.workspace.files]
  );
  const documentClients = useWorkspaceDocumentClients(props.workspace.id);
  const [clientError, setClientError] = React.useState<string | null>(null);

  const clientParts = (document: WorkspaceDocument) => {
    if (!documentClients.ready) return { note: null, menu: null };
    const clients = documentClients.clientsOf(document.id);
    const note = documentClientNote(clients, documentClients.workspaceClients);
    return {
      note: note && (
        <span className="block truncate text-[11px] italic text-neutral-500 dark:text-neutral-400">
          {note}
        </span>
      ),
      menu: (
        <WorkspaceDocumentClientMenu
          workspaceId={props.workspace.id}
          document={document}
          clients={clients}
          workspaceClients={documentClients.workspaceClients}
          onChanged={() => void documentClients.refetch()}
          onError={setClientError}
        />
      )
    };
  };

  if (artifacts.length === 0 && files.length === 0) {
    return (
      <OutlineEmptyState>
        Documents Cara drafts and files you attach will appear here.
      </OutlineEmptyState>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <FormError message={clientError} className="px-2 text-xs text-red-600 dark:text-red-400" />
      {artifacts.length > 0 && (
        <div>
          <OutlineSectionLabel>Documents</OutlineSectionLabel>
          <ul className="flex flex-col">
            {artifacts.map((artifact) => {
              const Icon = artifactIcon(artifact.type);
              const title = getArtifactTitle(artifact);
              const client = clientParts({ kind: 'artifact', id: artifact.id, title });
              return (
                <li key={artifact.id} className="flex items-start">
                  <OutlineRow
                    onClick={() => props.onOpenArtifact(artifact)}
                    className="min-w-0 flex-1 pl-2"
                  >
                    <Icon className="mt-0.5 size-3.5 shrink-0 text-neutral-400 dark:text-neutral-500" />
                    <span className="min-w-0 flex-1">
                      <span
                        className="block truncate text-xs font-medium text-neutral-800 dark:text-neutral-200"
                        title={title}
                      >
                        {title}
                      </span>
                      <span className="block truncate text-[11px] text-neutral-500 dark:text-neutral-400">
                        {artifact.templateForm?.label || getDisplayLabel(artifact.type)}
                      </span>
                      {client.note}
                    </span>
                  </OutlineRow>
                  {client.menu}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {files.length > 0 && (
        <div>
          <OutlineSectionLabel>Attachments</OutlineSectionLabel>
          <ul className="flex flex-col">
            {files.map((file) => {
              const name = attachmentName(file.name);
              const client = clientParts({ kind: 'file', id: file.id, title: name });
              return (
                <li key={file.id} className="flex items-start">
                  <a
                    href={file.url || ''}
                    target="_blank"
                    rel="noreferrer"
                    className={clsx(
                      'flex min-w-0 flex-1 items-start gap-2 rounded-md py-1 pl-2 pr-2',
                      // 'hover:bg-neutral-100/80 dark:hover:bg-neutral-800/80'
                      'hover:underline'
                    )}
                  >
                    <PaperClipIcon className="mt-0.5 size-3.5 shrink-0 text-neutral-400 dark:text-neutral-500" />
                    <span className="min-w-0 flex-1">
                      <span
                        className="block truncate text-xs text-neutral-600 dark:text-neutral-400"
                        title={name}
                      >
                        {name}
                      </span>
                      {client.note}
                    </span>
                  </a>
                  {client.menu}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

const VIEWS = [
  { key: 'messages', label: 'Messages', icon: ListBulletIcon },
  { key: 'documents', label: 'Documents', icon: DocumentTextIcon }
] as const;

type ViewKey = (typeof VIEWS)[number]['key'];

/**
 * One number for the panel's box, for how far its tab travels, and for whether
 * it fits beside the artifact viewer, so the three cannot drift.
 */
const PANEL_WIDTH = OUTLINE_RAIL_WIDTH;

const DOT_ROWS = [5, 10, 15];

const EASE_QUINT = [0.22, 1, 0.36, 1] as const;

/**
 * The toggle's icon, drawn by hand so it can morph.
 *
 * Swapping two heroicons would cross-fade one shape through another; here the
 * list's own strokes become the X. The outer two rules swing into the
 * diagonals, the middle rule shortens to nothing at the centre, and the bullets
 * shrink away, so what survives is continuous rather than redrawn. Endpoints
 * move as attributes rather than as a rotation, which keeps the stroke weight
 * even through the swing.
 */
const OutlineToggleIcon = (props: { open: boolean }) => {
  const transition = { duration: 0.28, ease: EASE_QUINT };
  const line = { strokeWidth: 1.8, strokeLinecap: 'round' as const, stroke: 'currentColor' };
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="size-4">
      {DOT_ROWS.map((y) => (
        <motion.circle
          key={y}
          cx={3.25}
          cy={y}
          fill="currentColor"
          initial={false}
          animate={{ r: props.open ? 0 : 1.1, opacity: props.open ? 0 : 1 }}
          transition={transition}
        />
      ))}
      <motion.line
        {...line}
        initial={false}
        animate={props.open ? { x1: 5, y1: 5, x2: 15, y2: 15 } : { x1: 7, y1: 5, x2: 17, y2: 5 }}
        transition={transition}
      />
      <motion.line
        {...line}
        initial={false}
        animate={
          props.open
            ? { x1: 10, y1: 10, x2: 10, y2: 10, opacity: 0 }
            : { x1: 7, y1: 10, x2: 17, y2: 10, opacity: 1 }
        }
        transition={transition}
      />
      <motion.line
        {...line}
        initial={false}
        animate={props.open ? { x1: 5, y1: 15, x2: 15, y2: 5 } : { x1: 7, y1: 15, x2: 17, y2: 15 }}
        transition={transition}
      />
    </svg>
  );
};

/**
 * Outline rail, opened and closed by the button that floats beside it.
 *
 * The rail stays open until the button is clicked again, because everything it
 * is for -- jumping between two sections, reading what you jumped to -- takes
 * the pointer back to the conversation. The button travels with the panel
 * rather than fading out, so the control that closes the rail is the one that
 * opened it, a constant distance from its edge.
 *
 * It sits on the left because the conversation's scrollbar runs down the right,
 * where a floating control would land on top of it, and because the artifact
 * viewer already owns that side.
 *
 * An open rail takes its width out of the row rather than covering the
 * conversation: the wrapper widens as the panel slides in, so the column beside
 * it narrows by exactly the panel while the panel itself holds still. When the
 * artifact viewer is wide enough that taking the rail's width would leave the
 * conversation under its minimum, the rail floats over the conversation
 * instead of squeezing it, and dismisses itself once a heading is picked or the
 * composer takes focus, since it is covering what the reader has just asked
 * for.
 * Only the wrapper's width changes between the two; the panel is in the same
 * place either way.
 *
 * The panel stays mounted and parked off-screen rather than being conditionally
 * rendered, so closing animates symmetrically with opening.
 */
export const WorkspaceOutlinePanel = (props: {
  workspace: Workspace;
  /** Whether the artifact viewer leaves too little of the row for the rail to take its width. */
  overlayConversation: boolean;
  onOpenArtifact: (artifact: Artifact) => void;
}) => {
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState<ViewKey>('messages');

  // Reading the outline means parsing every response's markdown, which is worth
  // nothing while the rail is closed. Mounting on open and unmounting only once
  // the slide-out has finished keeps that work off the streaming path without
  // emptying the panel in front of the user on the way out. It also leaves
  // nothing focusable inside the closed panel, which `aria-hidden` would
  // otherwise hide from a screen reader while the keyboard could still reach it.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setMounted(true);
    }
  }, [open]);

  // Writing a follow-up is the other thing that wants the conversation back, so
  // while the rail is covering it, focus landing in the composer closes the rail
  // just as picking a heading does. Focus rather than a click, so tabbing into
  // the box counts too.
  React.useEffect(() => {
    if (!open || !props.overlayConversation) {
      return;
    }
    const dismiss = (event: FocusEvent) => {
      if (event.target instanceof Element && event.target.closest(COMPOSER_SELECTOR)) {
        setOpen(false);
      }
    };
    document.addEventListener('focusin', dismiss);
    return () => document.removeEventListener('focusin', dismiss);
  }, [open, props.overlayConversation]);

  return (
    // Above the conversation and the jump buttons it overlaps, below the top
    // bar and the sidebar, whose hover flyouts open over this column.
    <motion.div
      className="hidden lg:block relative h-full shrink-0 z-10"
      initial={false}
      animate={{ width: open && !props.overlayConversation ? PANEL_WIDTH : 0 }}
      transition={{ duration: 0.28, ease: EASE_QUINT }}
    >
      <motion.aside
        id="workspace-outline"
        aria-label="Conversation outline"
        aria-hidden={!open}
        initial={false}
        animate={{ x: open ? 0 : '-100%', opacity: open ? 1 : 0 }}
        transition={{ duration: 0.28, ease: EASE_QUINT }}
        onAnimationComplete={() => !open && setMounted(false)}
        style={{ width: PANEL_WIDTH }}
        className={clsx(
          'absolute top-0 left-0 flex h-full flex-col p-4',
          'border-r border-neutral-200/60 dark:border-neutral-700/60',
          'bg-white/80 dark:bg-neutral-800/80 backdrop-blur-md',
          // Nothing moves aside for it here, so the edge has to come from the panel.
          props.overlayConversation && 'shadow-xl',
          !open && 'pointer-events-none'
        )}
      >
        {mounted && (
          <>
            <SegmentedControl
              value={view}
              onChange={setView}
              aria-label="Outline contents"
              className="shrink-0 bg-neutral-200 dark:bg-neutral-700"
            >
              {VIEWS.map((option) => (
                <SegmentedControlOption
                  key={option.key}
                  value={option.key}
                  icon={option.icon}
                  className="text-xs py-1.5"
                >
                  {option.label}
                </SegmentedControlOption>
              ))}
            </SegmentedControl>
            {/* The outline outgrows the panel on a long conversation, so the list scrolls under a fixed switcher. */}
            <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
              {view === 'messages' && (
                <WorkspaceOutlineMessageList
                  workspace={props.workspace}
                  // Overlaying, the panel is sitting on the place the click is
                  // sending the eye, so it leaves. In its own column there is
                  // nothing to get out of the way of, and the scroll it started
                  // is still running under a rail that never moved.
                  onSelect={props.overlayConversation ? () => setOpen(false) : undefined}
                />
              )}
              {view === 'documents' && (
                <WorkspaceOutlineDocumentList
                  workspace={props.workspace}
                  onOpenArtifact={props.onOpenArtifact}
                />
              )}
            </div>
          </>
        )}
      </motion.aside>
      {/*
        The button travels the panel's width so it keeps the same gap from the
        panel's edge whether the rail is open or closed, and it moves on the
        panel's own curve and duration so the pair arrives together instead of
        one chasing the other.
      */}
      <motion.button
        type="button"
        aria-label="Conversation outline"
        aria-expanded={open}
        aria-controls="workspace-outline"
        onClick={() => setOpen(!open)}
        initial={false}
        animate={{ x: open ? PANEL_WIDTH - 8 : 0 }}
        transition={{ duration: 0.28, ease: EASE_QUINT }}
        className={clsx(
          'absolute top-4 left-6 flex items-center justify-center rounded-full p-2',
          'bg-white dark:bg-neutral-800 shadow hover:shadow-lg transition-shadow duration-150',
          'cursor-pointer',
          open ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-500 dark:text-neutral-400'
        )}
      >
        <OutlineToggleIcon open={open} />
      </motion.button>
    </motion.div>
  );
};
